// ─────────────────────────────────────────────────────────────────────────────
// utils/solutionCache.ts
// Centralized accessors for the two window-level caches used across
// ViewSolutionDetails component instances.
//
// Using window-level Maps intentionally: they survive React component
// unmounts so previously fetched agent data is reused without re-fetching.
// ─────────────────────────────────────────────────────────────────────────────

import { SolutionSyncCacheEntry } from './types';

/** 30-minute TTL for automatic cache invalidation */
export const CACHE_TIMEOUT = 30 * 60 * 1000;

// ─── Sync-state cache (timestamp + flags) ────────────────────────────────────

const getSyncCache = (): Map<string, SolutionSyncCacheEntry> => {
  if (!(window as any).solutionSyncCache) {
    (window as any).solutionSyncCache = new Map<string, SolutionSyncCacheEntry>();
  }
  return (window as any).solutionSyncCache;
};

export const getSyncCacheEntry = (solutionId: string): SolutionSyncCacheEntry | undefined =>
  getSyncCache().get(solutionId);

export const setSyncCacheEntry = (solutionId: string, entry: SolutionSyncCacheEntry): void => {
  getSyncCache().set(solutionId, entry);
};

export const markSolutionSynced = (
  solutionId: string,
  wasManual = false,
  hasCompleteData = false
): void => {
  setSyncCacheEntry(solutionId, {
    timestamp: Date.now(),
    wasManualSync: wasManual,
    hasAgentData: hasCompleteData,
  });

  if (hasCompleteData) {
    sessionStorage.setItem(
      `simulation_sync_agent_updated_at_${solutionId}`,
      new Date().toISOString()
    );
  }
};

// ─── Agent-data cache (full solution + agent blobs) ──────────────────────────

export interface AgentCacheEntry {
  solutionData: any;
  agentData: {
    agentLlmResponse: any[];
    llmResponseData: any[];
  };
  /** ISO timestamp of agent.updated_at at write time — used to detect version restore */
  agentUpdatedAt?: string | null;
}

const getAgentCache = (): Map<string, AgentCacheEntry> => {
  if (!(window as any).agentDataCache) {
    (window as any).agentDataCache = new Map<string, AgentCacheEntry>();
  }
  return (window as any).agentDataCache;
};

export const getAgentCacheEntry = (solutionId: string): AgentCacheEntry | undefined =>
  getAgentCache().get(solutionId);

export const setAgentCacheEntry = (
  solutionId: string,
  entry: AgentCacheEntry,
  agentUpdatedAt?: string | null
): void => {
  getAgentCache().set(solutionId, { ...entry, agentUpdatedAt: agentUpdatedAt ?? null });
};

/**
 * Patches only the `solutionData` portion of an existing cache entry
 * without touching the agentData blob.  Used after inline edits / saves.
 */
export const updateCachedSolutionData = (latestSolutionData: any): void => {
  if (!latestSolutionData?.id) return;
  const solutionId = String(latestSolutionData.id);
  const existing = getAgentCacheEntry(solutionId);
  if (!existing) return;

  // Guard: don't merge if the agent version changed (version restore scenario)
  const incomingAgentVersion = latestSolutionData?.agent?.updated_at ?? null;
  if (incomingAgentVersion !== existing.agentUpdatedAt) {
    // Version changed — drop the cached agent data entirely, let the next sync rebuild it
    getAgentCache().delete(solutionId);
    return;
  }

  setAgentCacheEntry(
    solutionId,
    {
      ...existing,
      solutionData: { ...existing.solutionData, ...latestSolutionData },
    },
    existing.agentUpdatedAt
  );
};

/**
 * Returns true when a cached sync entry exists, is recent, and has complete
 * agent data — meaning we can skip a fresh sync call.
 */
export const isCacheValid = (solutionId: string): boolean => {
  const syncEntry = getSyncCacheEntry(solutionId);
  if (!syncEntry || syncEntry.wasManualSync) return false;

  const isRecent = Date.now() - syncEntry.timestamp < CACHE_TIMEOUT;
  return isRecent && syncEntry.hasAgentData;
};
